import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks";
import { PatientReviApi } from "../../api/revi/patientApi";
import { TherapistReviApi } from "../../api/revi/therapistApi";
import { message } from "antd";

const patientApi = new PatientReviApi();
const therapistApi = new TherapistReviApi();

export function usePatientController() {
  // Los avisos de este hook los ve la institucion: son interfaz.
  const { t } = useTranslation("misc");
  const { user, accessToken } = useAuth();

  const [patients, setPatients] = useState([]);
  const [therapistsList, setTherapistsList] = useState([]); // Para el Select
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);

  // Obtener ID de institución (Identity Shield)
  const getInstitutionId = () => {
    const userData = user?.user || user;
    if (userData?.institutions?.length > 0) {
      return typeof userData.institutions[0] === "object"
        ? userData.institutions[0]._id
        : userData.institutions[0];
    }
    return null;
  };

  const institutionId = getInstitutionId();

  // Carga inicial de datos
  const loadData = useCallback(
    async (page = 1) => {
      if (!institutionId) return;
      try {
        setLoading(true);

        // Cargar pacientes y terapeutas en paralelo
        const [patientsRes, therapistsRes] = await Promise.all([
          patientApi.getByInstitution(accessToken, institutionId, {
            page,
            limit: 12,
          }),
          therapistApi.getByInstitution(accessToken, institutionId, {
            page: 1,
            limit: 100,
          }), // Traemos todos para el select
        ]);

        setPatients(patientsRes.patients || []);
        setPagination({
          page: patientsRes.pagination.page,
          totalPages: patientsRes.pagination.totalPages,
          totalDocs: patientsRes.pagination.totalDocs,
        });

        setTherapistsList(therapistsRes.therapists || []);
      } catch (error) {
        console.error(error);
        message.error(t("patients.loadError"));
      } finally {
        setLoading(false);
      }
    },
    // `t` solo cambia al cambiar de idioma; su identidad es estable el resto
    // del tiempo, asi que no provoca recargas de mas.
    [accessToken, institutionId, t],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const openNewModal = () => {
    setEditingPatient(null);
    setShowModal(true);
  };

  const openEditModal = (patient) => {
    setEditingPatient(patient);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPatient(null);
  };

  const handleDelete = async (patient) => {
    try {
      await patientApi.delete(accessToken, patient._id);
      message.success(t("patients.deleted", { nombre: patient.name }));
      loadData(pagination.page);
    } catch (error) {
      message.error(error.message);
    }
  };

  return {
    patients,
    therapistsList, // Exponemos la lista para el formulario
    pagination,
    loading,
    showModal,
    editingPatient,
    institutionId,
    loadData,
    openNewModal,
    openEditModal,
    closeModal,
    handleDelete,
  };
}
